import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { ContractChangePayload } from './types'
import { UserInfo, CareerPathDto, LatestContractDtoFragment } from '../../../types'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import { CareerPathPopover } from '../../../components/custom_components/CarrerPathPopover/CareerPathPopover'
import { createCareerPathEvaluation, filterCareerPathReferencesByLevel } from './careerPath/careerpath-util'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerText from '../../../components/custom_components/Text'
import { useCurrency } from '../../../context/currency-context'

interface Props {
  raw: string
  user: UserInfo
  contract?: LatestContractDtoFragment
  careerPathData?: CareerPathDto[] | null
  nationalContractReferenceLevel?: string
  isConclusion?: boolean
  isPreparation?: boolean
  hasCareerPath?: boolean
}

const ContractChangeListItem = ({ t, label, item }) => {
  return (
    <li>
      <PerFlexRow>
        <PerText appearance="info" mr="xxs">
          {`${t(`appraisalMeeting.contract.changes.table.${label}`)}: `}
        </PerText>
        {item}
      </PerFlexRow>
    </li>
  )
}

export const ContractChangeList: FC<Props> = ({
  raw,
  user,
  careerPathData,
  nationalContractReferenceLevel,
  isConclusion = false,
  isPreparation = false,
  hasCareerPath = false,
  contract,
}) => {
  const [t] = useTranslation()
  const { formatCurrency } = useAmountAndCurrency(user.locale)
  const { currency: fallbackCurrency } = useCurrency()
  const change: ContractChangePayload = JSON.parse(raw)

  const salaryCurrency = contract && contract.annualSalary ? contract.annualSalary.currency : fallbackCurrency
  const bonusCurrency = contract && contract.targetBonus ? contract.targetBonus.currency : fallbackCurrency

  let careerPathReference: { bonus: CareerPathDto; salary: CareerPathDto } | undefined = undefined
  if (contract && careerPathData && contract.employeeLevel) {
    careerPathReference = filterCareerPathReferencesByLevel(careerPathData, contract.employeeLevel.level)
  }
  let newCareerPath
  if (!!contract && careerPathData) newCareerPath = createCareerPathEvaluation(change, contract, careerPathData)
  return (
    <ul>
      {change.nationalReferenceLevel && (
        <ContractChangeListItem
          t={t}
          label="employeeNationalLevel"
          item={`${t(`employee.nationalContract.shortType.${nationalContractReferenceLevel}`)} ${t(
            `employee.nationalContract.level.${change.nationalReferenceLevel}`,
          )}`}
        />
      )}
      {change.governmentJobTitle && <ContractChangeListItem t={t} label="governmentJobTitle" item={change.governmentJobTitle} />}
      {change.employeeLevel && (
        <ContractChangeListItem t={t} label="employeeLevel" item={t(`employee.contract.level.${change.employeeLevel}`)} />
      )}
      {change.salary !== undefined && (
        <ContractChangeListItem t={t} label="salary" item={formatCurrency(change.salary, salaryCurrency, true)} />
      )}
      {change.bonus !== undefined && (
        <ContractChangeListItem t={t} label="bonus" item={formatCurrency(change.bonus, bonusCurrency, true)} />
      )}
      {hasCareerPath && !!isPreparation && !isConclusion && !!contract && careerPathReference && newCareerPath && (
        <ContractChangeListItem
          t={t}
          label="careerPath"
          item={
            <CareerPathPopover
              currency={contract.currencyForPayments}
              careerPath={newCareerPath}
              bonusReference={careerPathReference.bonus}
              salaryReference={careerPathReference.salary}
            />
          }
        />
      )}
    </ul>
  )
}
